// Spike 002 (green valley): the composition's entry (engine/docs/spikes/002-green-valley/brief.md). Phase A1 joins
// four streams here:
// - the boot shell and game flow (src/boot/, gates A1.1, A1.2): Rapier and Recast as timed boot steps behind a
//   loading screen, then title and play;
// - content and the kernel (src/content/, src/core/, gates A1.4, A1.5): JSON tables with schemas, the strings
//   table, fail-soft systems, typed events, stub services;
// - the deterministic sim (src/sim/, gate A1.3): a Rapier scene stepped on the runtime's 120 Hz fixed step, with a
//   versioned save, a state hash and input record and replay;
// - the harness (src/harness/, gate A1.6): invariants after every step, scenarios as save fixtures, debug views,
//   the compile watchdog, planted-defect switches.
// Phase A2 adds the input (src/input/, gate A2.1): the input contract from the content, contexts, the buffer, the
// rebinding model, the touch overlay and the prompt bar; the sim reads its actions. The test valley (src/valley/,
// gates A2.2, A2.3) and the camera (src/camera/, src/valley/camera.ts, gate A2.4) run in the sim.
// Phase A3 adds the figure and the low-poly look (src/figure/, gates A3.1 to A3.3; the lab at `?scene=figure`).
// Phase A4 adds the saves and the screens (src/save/, src/ui/, gates A4.1, A4.2), joined in src/save-ui.ts.
// Phase B's feedback stream adds targeting and the impact director (src/targets/, src/feedback/, gates B3 and B6;
// the lab at `?scene=targets`) and the lock button's reveal on the touch overlay.
// Phase B5 adds the bodies (src/bodies/): the pose layer, hit reactions and ragdolls; the lab at `?scene=ragdoll`.
// Phase C's sky stream adds the clock, the weather and the look per time (src/sky/, gate C3); the lab at `?scene=sky`.
// Phase C5 adds the things (src/things/): gathering, breakables, pickups, interaction and the navmesh's tile rebuilds in
// slices; the lab at `?scene=things`, with the interaction prompt.
// Phase D2's audio stream adds the page's one audio engine (src/audio/, gate D2.4: sounds, ambience, music, babble on
// the boot's AudioContext) and accessibility (src/a11y/, gate D2.3: text size and contrast everywhere, subtitles and
// captions, the recap on load, pause anywhere, the flash cap).
// Phase D1's town stream adds the townsfolk, their routines, the cutscenes and the crowd's animation LOD (src/town/); the
// scene at `?scene=town`.
// Phase D2 adds the HUD, world-space UI, discovery, the minimap and the map screen (src/hud/; the lab at `?scene=hud`).
// Phase E1 adds meta and onboarding (src/meta/): achievements, the codex, tutorial prompts and photo mode.
// The agent handle is `window.__rpg`, in dev builds only (src/harness/dev.ts, decision 0006).
import * as THREE from "three";
import { createRuntime } from "@rpg/runtime";
import { startBoot } from "./boot";
import { setupContent } from "./core/setup";
import { inputFromContent, setupInput } from "./input";
import { mountSaveUi } from "./save-ui";
import { figureFaults, figureLabFor } from "./figure";
import { mountSim } from "./sim";
import { cameraFaultGroup, pointerLockOnPress } from "./camera";
import { mountCameraLab } from "./camera/lab";
import { mountTalkLab } from "./talk/lab";
import { creaturesLabFor } from "./creatures/lab";
import { COMBAT_TOUCH_BUTTONS, combatFaultGroup } from "./combat";
import { isTargetsScene, mountLockButton, targetingFaultGroup, targetsLabFor } from "./targets";
import { feedbackFaultGroup } from "./feedback";
import { bodiesFaults } from "./bodies";
import { worldFaults } from "./world";
import { placesFaultGroup } from "./places";
import { skyFaults } from "./sky";
import { mountInteractPrompt, thingsFaultGroup } from "./things";
import { mountRpgUi, rpgFaultGroup } from "./rpg";
import { mountAudio } from "./audio";
import { a11yFaultGroup, mountAccessibility, recapScreen } from "./a11y";
import { townFaults } from "./town";
import { hudFaults, mountHud } from "./hud";
import { mountStoryUi, storyFaults } from "./story";
import { mountTelemetry } from "./telemetry";
import { metaFaultGroup, mountMeta } from "./meta";

let ready = false;
// the boot shell starts first: it captures errors from the start (content issues logged while booting reach
// `__rpg.errors`) and shows the loading screen while Rapier and Recast load
const boot = startBoot();

async function main(): Promise<void> {
  const content = setupContent();
  const { RAPIER } = await boot.modules;

  const renderer = new THREE.WebGLRenderer({ antialias: true });
  renderer.setPixelRatio(Math.min(devicePixelRatio, 2));
  renderer.setSize(innerWidth, innerHeight);
  document.body.appendChild(renderer.domElement);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color("#cfe3c0");
  const camera = new THREE.PerspectiveCamera(55, innerWidth / innerHeight, 0.1, 500);
  camera.position.set(0, 9, 18);
  camera.lookAt(0, 1, 0);
  scene.add(new THREE.HemisphereLight("#fff4d6", "#5d7a3a", 1.2));

  // phase B: the split hit-stop (the sim freezes, the view and input keep running) when the tuning asks for it
  const freeze = content.content.registry.get("tuning", "runtime.hitStopMode.freeze")?.value === 1;
  const runtime = createRuntime({ stepHz: 120, hitStopMode: freeze ? "freeze" : "scale" });
  // the input first (gate A2.1): the sim reads its actions, buffer included
  // the UI (A4) is mounted below; the input and the camera read it through these once it exists
  let menuOpen = (): boolean => false;
  let setting = (_id: string): unknown => undefined;
  const input = setupInput({ runtime, registry: content.content.registry, t: content.content.t, flow: boot.flow, menuOpen: () => menuOpen() });
  // the one kernel game lives in the sim host (A2): the core module, the scene's modules, the planted thrower
  const tuning = content.content.registry;
  // look input for the camera (gate A2.4): the input's `look` action, consumed once per rendered frame on real time
  // (decision 0011); outside a sim step, so it is never on an input tape (the yaw it turns is: src/valley/scene.ts)
  // phase E1: a look the camera consumed is the look tutorial's verb used
  let noteLook = (): void => {};
  const look = (realDt: number) => {
    const d = input.input.actions.consumeDelta("look", realDt);
    if (d.x !== 0 || d.y !== 0) noteLook();
    return d;
  };
  // phase A3: the figure lab (`?scene=figure`) draws its own scene in the low-poly look; the sim steps offstage
  const lab = figureLabFor(location.search, { runtime, renderer, rapier: RAPIER, content: content.content, tier: boot.report().tier });
  // IMP-0059: the creatures lab (`?scene=creatures`) draws its own scene the same way: the creature presets in the look
  const creatures = creaturesLabFor(location.search, { runtime, renderer, registry: content.content.registry, tier: boot.report().tier });
  const sim = mountSim({
    rapier: RAPIER,
    runtime,
    scene: lab ? lab.offstage : creatures ? creatures.offstage : isTargetsScene(location.search) ? new THREE.Scene() : scene,
    camera,
    content: content.content,
    look,
    setting: (id) => setting(id),
    tuning: { get: (id) => tuning.get("tuning", id)?.value, ids: () => tuning.rows("tuning").map((r) => r.id) },
    actions: input.input.actions,
    renderer,
    tier: boot.report().tier,
  });
  // the default scene, and phase C's streamed valley (`?scene=world`), lock the pointer on a press
  const sceneParam = new URLSearchParams(location.search).get("scene");
  if (sceneParam === null || sceneParam === "world" || sceneParam === "town" || sceneParam === "hud" || sceneParam === "story") pointerLockOnPress(renderer.domElement);
  // phase B: the dojo uses the skill, big-move, block and weapon-switch buttons the touch layout keeps hidden till then
  // and since the merge so do the valley and the arena, where the fights are
  if (sim.scene === "dojo" || sim.scene === "valley" || sim.scene === "arena" || sim.scene === "world" || sim.scene === "things" || sim.scene === "market" || sim.scene === "town" || sim.scene === "hud" || sim.scene === "story") for (const id of COMBAT_TOUCH_BUTTONS) input.overlay.reveal(id, true);
  // phase B joined: the combat input context (the lock action) while an enemy is alert to the hero
  if (sim.engaged) {
    const engaged = sim.engaged;
    runtime.on("input", () => input.input.contexts.set("combat", engaged()), { name: "fight: combat context", order: -1e9 });
  }
  // gate A2.4: the camera lab (`?scene=camera`; null otherwise), its rig driving the main camera
  const cameraLab = mountCameraLab({ rapier: RAPIER, runtime, scene, camera, content: content.content, el: renderer.domElement, look, setting: (id) => setting(id) });
  // capability round 1 (IMP-0044): the talk lab (`?scene=talk`; null otherwise), the conversation and scene-player units from data
  const talkLab = mountTalkLab({ runtime, scene, camera });
  // phase B (gates B3, B6): the targets lab (`?scene=targets`; null otherwise) on the sim's kernel bus; the lock
  // button shows on the touch overlay in combat when lock-on is the setting
  const targets = targetsLabFor(location.search, { runtime, renderer, rapier: RAPIER, content: content.content, tier: boot.report().tier, events: sim.events, input, setting: (id) => setting(id), look });
  mountLockButton({ runtime, input, setting: (id) => setting(id) });
  // A world frozen under the title or a menu (the runtime paused; the screens are DOM on top) is drawn once, not
  // every frame: a phone's battery, and headless (SwiftShader) where one frame of the valley's look on the high tier
  // takes about 0.3 s, which starved the screens' pad polling. Deterministic advances always draw.
  let drawnWhilePaused = false;
  // phase E1: photo mode draws every frame over the paused game
  let photoActive = (): boolean => false;
  addEventListener("resize", () => void (drawnWhilePaused = false));
  runtime.on("render", (c) => {
    if (c.mode === "play" && runtime.paused && !photoActive()) {
      if (drawnWhilePaused) return;
      drawnWhilePaused = true;
    } else drawnWhilePaused = false;
    if (lab) lab.render();
    else if (creatures) creatures.render();
    else if (targets) targets.render();
    else if (sim.render) sim.render();
    else renderer.render(scene, camera);
  });
  // phase D2: the recap screen joins the UI's screens (accessibility binds its data below)
  const recap = recapScreen();
  const saveUi = mountSaveUi({ content: content.content, sim: sim.handle, renderer, boot, stepHz: 120, rebinding: input.input.rebinding, screens: recap.screens });
  menuOpen = () => saveUi.handle.ui.top() !== null;
  setting = (id) => saveUi.settings.get(id);
  // phase D2 (gate D2.4): the page's one audio engine, on the boot's AudioContext; (gate D2.3) accessibility
  const listener = (): THREE.Camera => lab?.camera ?? creatures?.camera ?? targets?.camera ?? camera;
  const audio = mountAudio({ runtime, content: content.content, events: sim.events, boot, camera: listener, sim, setting: (id) => saveUi.settings.get(id), onSettings: (fn) => void saveUi.settings.subscribe(fn), tier: boot.report().tier, playing: () => boot.flow.state() === "play" && !menuOpen() });
  const a11y = mountAccessibility({
    runtime,
    content: content.content,
    events: sim.events,
    ui: saveUi.ui,
    settings: saveUi.settings,
    flow: boot.flow,
    saves: saveUi.saves,
    contexts: input.input.contexts,
    onCaption: (fn) => void audio.engine.onCaption(fn),
    region: () => {
      const p = listener().position;
      return (sim.handle.world?.regionAt(p.x, p.z) as string | null | undefined) ?? null;
    },
    // phase D joined: the recap reads the story's objective and event log; `talk` is its talk with a townsperson
    story: () => (sim.story ? { objective: () => sim.story!.objective(), deeds: (n: number) => sim.story!.handle.deeds(n) } : null),
    talk: sim.story ? (npc: string) => sim.story!.handle.talk(npc) : null,
    ...(sim.story ? { speakerName: (id: string) => sim.story!.npcName(id), boxOpen: () => saveUi.ui.top() === "dialogue" } : {}),
    touch: () => input.input.controls.device === "touch",
    recap,
  });
  // phase C5: the interaction prompt over what the chooser picked (the things lab; the valley once things join it)
  let interactState: (() => { shown: boolean }) | null = null;
  if (sim.things) {
    const prompt = mountInteractPrompt({ runtime, input: input.input, overlay: input.overlay, glyphs: inputFromContent(content.content.registry).glyphs, content: content.content, camera, things: sim.things, playing: () => boot.flow.state() === "play" && !menuOpen() && !sim.handle.town?.cutscene.active() });
    sim.setPrompt?.(prompt.state);
    interactState = prompt.state;
  }
  // phase D1: the rpg layer's screens (the market lab; the valley once the rpg layer joins it): the inventory and
  // skills in the pause menu, the shop, forge and pot from the world's stations and NPCs
  if (sim.rpg) {
    const rpgUi = mountRpgUi({ runtime, content: content.content, rpg: sim.rpg, addScreens: saveUi.addScreens, push: (id, p) => saveUi.ui.push(id, p), top: () => saveUi.ui.top(), flow: boot.flow });
    sim.setRpgUi?.(rpgUi);
  }
  // phase D2: the HUD, the world-space UI, the minimap and the map screen (the hud lab, `?scene=hud`; src/hud/)
  const hud = sim.hud && sim.rpg ? mountHud({ runtime, camera, content: content.content, sim, ui: saveUi.ui, settings: saveUi.settings, flow: boot.flow, input: input.input, overlay: input.overlay, rpg: () => sim.rpg!()!, npcAt: (id) => { const n = sim.handle.town?.npc(id); return n && n.mode !== "inside" ? [n.x, n.y, n.z] : null; }, cutscene: () => sim.handle.town?.cutscene.active() ?? false, objective: () => sim.story?.objective() ?? null }) : null;
  // phase D's story: the dialogue box and the quest log on the UI, the notice strip
  const storyUi = sim.story ? mountStoryUi({ ui: saveUi.ui, addScreens: saveUi.addScreens, content: content.content, runtime, story: sim.story, playing: () => boot.flow.state() === "play", pause: () => boot.flow.pause() }) : null;
  // phase E (ops, gate E2): opt-in telemetry on the kernel's events, the page's errors and the frame times (src/telemetry/)
  const telemetry = mountTelemetry({ runtime, renderer, content: content.content, events: sim.events, boot, saves: saveUi.saves, settings: saveUi.settings, settingsReady: () => saveUi.handle.ui.ready(), ui: saveUi.ui, addScreens: (more) => saveUi.addScreens(more), systems: () => sim.handle.kernel().systems, tier: () => saveUi.handle.ui.settings.applied().tier });
  // phase E1: achievements, the codex, tutorial prompts and photo mode over the joined valley's meta layer (src/meta/);
  // in dev builds photo mode's deliberate compiles are excused to the harness's watchdog (set once the handle exists)
  let excuse = (_reason: string): void => {};
  let editorOpen = (): boolean => false;
  const meta = sim.meta
    ? mountMeta({
        runtime,
        content: content.content,
        layer: sim.meta,
        events: sim.events,
        ui: saveUi.ui,
        addScreens: saveUi.addScreens,
        settings: saveUi.settings,
        saves: saveUi.saves,
        flow: boot.flow,
        input: input.input,
        glyphs: inputFromContent(content.content.registry).glyphs,
        conditions: sim.story?.layer().story.conditions ?? null,
        glyphCount: () => sim.rpg?.()?.glyphs() ?? 0,
        play: () => ({
          airborne: ((m) => m === "air" || m === "glide")(sim.photo?.heroMode() ?? "ground"),
          foe: sim.engaged?.() ?? false,
          target: interactState?.().shown ?? false,
          found: sim.hud?.world.discovery().state().discovery.order.length ?? 0,
        }),
        cutscene: () => sim.handle.town?.cutscene.active() ?? false,
        photo: sim.photo ? { hooks: sim.photo, scene, camera, renderer, tier: boot.report().tier } : null,
        // dev builds: photo mode's deliberate compiles go to the harness's watchdog as excused (nothing in production)
        excuse: (reason: string) => excuse(reason),
        // dev builds: the editor and photo mode share the camera's overlay layer, so one waits while the other is up
        photoBlocked: () => editorOpen(),
      })
    : null;
  if (meta) {
    noteLook = meta.noteLook;
    photoActive = meta.photoActive;
  }
  // phase B4: the arena's difficulty follows the settings' choice (a fresh fight on a change), unless the URL names one
  // and so does the valley's fight (phase B joined)
  const arenaKeys = (sim.handle as { arena?: { useDifficulty(id: string): void }; fight?: { useDifficulty(id: string): void } }).arena ?? (sim.handle as { fight?: { useDifficulty(id: string): void } }).fight;
  if (arenaKeys && !new URLSearchParams(location.search).has("difficulty")) {
    const useDifficulty = () => arenaKeys.useDifficulty(String(saveUi.settings.get("accessibility.difficulty")));
    useDifficulty();
    saveUi.settings.subscribe(useDifficulty);
  }
    boot.ready({ runtime, renderer, scene: lab?.scene ?? targets?.scene ?? scene, camera: lab?.camera ?? targets?.camera ?? camera, screens: saveUi.flowScreens, screenOpen: () => menuOpen() });
  // phase D joined: the page's first play is the story's new game (its opening's trigger waits for it; a save that has
  // seen the opening doesn't ask again). `?opening=off` starts as after the opening, for checks that don't watch it
  // (agent call, pending the user)
  const storyKeys = sim.handle.story;
  if (storyKeys) {
    const opening = new URLSearchParams(location.search).get("opening") !== "off";
    let asked = false;
    runtime.on("input", () => {
      if (asked || boot.flow.state() !== "play") return;
      asked = true;
      storyKeys.newGame(opening);
    }, { name: "story: new game", order: -1e9 });
  }
  const townKeys = sim.handle.town;
  if (townKeys) {
    // phase D joined: a playing cutscene holds the dialogue input context (gameplay actions blocked under it; pause, in
    // the system context above it, stays live), and the advance action skips it (agent call)
    let inScene = false;
    runtime.on("input", () => {
      const on = townKeys.cutscene.active();
      // the prompt bar and the touch overlay stay hidden under a scene (the input's own sync shows them each frame)
      if (on) {
        input.prompt.show(false);
        input.overlay.show(false);
      }
      if (on === inScene) return;
      inScene = on;
      if (on) input.input.contexts.push("dialogue");
      else input.input.contexts.pop("dialogue");
    }, { name: "town: cutscene context", order: -1e9 + 1 });
    input.input.contexts.on("advance", () => {
      if (inScene && boot.flow.state() === "play") townKeys.cutscene.skip();
    });
  }
  addEventListener("resize", () => {
    renderer.setSize(innerWidth, innerHeight);
    camera.aspect = innerWidth / innerHeight;
    camera.updateProjectionMatrix();
  });

  // dev builds only: the harness and the handle, with every module's keys and planted defects (src/harness/dev.ts)
  if (import.meta.env.DEV) {
    const { installDevHandle } = await import("./harness/dev");
    const dev = installDevHandle({
      runtime,
      renderer,
      scene: lab?.scene ?? targets?.scene ?? scene,
      boot,
      sim,
      ready: () => ready && boot.booted(),
      state: () => ({ step: runtime.clock.step, rapier: RAPIER.version(), sim: sim.state(), input: input.state(), ...(cameraLab ? { camera: cameraLab.state() } : {}), ...(lab ? { figure: lab.state() } : {}), ...(targets ? { targets: targets.state() } : {}), ...(talkLab ? { talk: talkLab.state() } : {}), ...(creatures ? { creatures: creatures.state() } : {}) }),
      keys: { ...content.handle, ...saveUi.handle, input: input.handle, audio: audio.handle, a11y: a11y.handle, ...(cameraLab ? { camera: cameraLab.handle } : {}), ...(lab ? { figure: lab.handle } : {}), ...(targets ? { targets: targets.handle } : {}), ...(talkLab ? { talk: talkLab } : {}), ...(creatures ? { creatures: creatures.handle } : {}), ...(hud ? hud.handle : {}), ...(storyUi ? { storyUi } : {}), telemetry: telemetry.handle, ...(meta ? { meta: meta.handle } : {}) },
      camera,
      content: content.content,
      faultGroups: [...saveUi.faultGroups, input.faults, cameraFaultGroup(), figureFaults, combatFaultGroup(runtime), targetingFaultGroup(), feedbackFaultGroup(), bodiesFaults, worldFaults, placesFaultGroup(), skyFaults, thingsFaultGroup(), rpgFaultGroup(), audio.faultGroup, a11yFaultGroup(), townFaults, hudFaults, storyFaults, telemetry.faultGroup, metaFaultGroup()],
      photoActive: () => meta?.photoActive() ?? false,
    });
    excuse = dev.excuse;
    editorOpen = dev.editorOpen;
  }
  runtime.start();
  ready = true;
}

main().catch((e) => boot.fail(e));
